import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { styled, SxProp, useResponsiveValue } from 'dripsy';

import {
  ScreenTopBar,
  TagHorizontalList,
  Separator,
  SCREEN_TOP_BAR_HEIGHT,
  TAG_ITEM_HEIGHT,
} from '@components';
import {
  HomeTabNavigatorNavigationPropType,
  HomeTabParamListType,
} from '@navigation/types';
import { screens } from '@navigation/screens';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { Tag } from '@models/Tags/TagModel';
import { useTagsModel } from '@models/Tags/TagsModel';
import { useAsyncInit } from '@hooks';
import {
  AnalyticsService,
  AnalyticGlobalSearchEventTypeEnum,
  ToastService,
} from '@services';
import { parseApiError } from '@utils';
import { SectionItemListTypeEnum } from '@constants';

export const HOME_SCREEN_TOB_BAR_HEIGHT_RESPONSIVE =
  SCREEN_TOP_BAR_HEIGHT + TAG_ITEM_HEIGHT + 12; // 12 is $3 + $3 from separators;

type NavigationPropType = HomeTabNavigatorNavigationPropType<
  keyof HomeTabParamListType
>;

const Container = styled(View)({
  bg: '$background0',
});

const tagListContainerStyle: SxProp = {
  px: ['$6', '$0'],
  justifyContent: 'center',
  flexGrow: 1,
};

const TagListContainer = styled(View)({
  overflow: [null, 'hidden'],
  px: [null, '$7', '5%'],
});

export const HomeScreenTopBar = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const tags = useTagsModel();
  const isSearchVisible = useResponsiveValue([false, true]);
  const nav = useNavigation<NavigationPropType>();

  useAsyncInit(async () => {
    try {
      await tags.recommended.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  const navigateToSearch = useCallback((text: string) => {
    nav.navigate(screens.SearchTabNavigator, {
      screen: screens.SearchTutorial,
      params: { query: text },
    });
    AnalyticsService.trackGlobalSearch(
      text,
      AnalyticGlobalSearchEventTypeEnum.Static,
    );
  }, []);

  const navigateToTaggedItems = useCallback((item: Tag) => {
    nav.push(screens.HubNavigator, {
      screen: screens.Items,
      params: {
        type: SectionItemListTypeEnum.Tag,
        id: item.id,
        name: item.name,
      },
    });
    AnalyticsService.trackTagClick(item.name);
  }, []);

  return (
    <Container>
      <ScreenTopBar
        title={t('home.greeting', {
          name: viewer.currentUser?.firstName ?? '',
        })}
        onSearchSubmit={navigateToSearch}
        isSearchVisible={isSearchVisible}
      />
      <Separator size="$5" />

      <TagListContainer>
        <TagHorizontalList
          contentContainerSx={tagListContainerStyle}
          onPress={navigateToTaggedItems}
          data={tags.recommended.asArray}
        />
      </TagListContainer>

      <Separator size="$3" />
    </Container>
  );
});
